import { useCallback, useEffect, useState } from "react";
import { TFormHealth } from "..";
import useMyMontepioContext from "../../../components/Context/useMyMontepioContext";
import { MGButton } from "../../../designSystem/components/MGButton";
import { MGLabel } from "../../../designSystem/components/MGLabel";
import { MGText } from "../../../designSystem/components/MGText";
import styles from "../Health.module.css";
import { MGCard } from "../../../designSystem/components/MGCard";
import { InfoIcon } from "lucide-react";
import { MGColors } from "../../../designSystem/colors";
import { MGResponsiveSheet } from "../../../designSystem/components/MGResponsiveSheet";
import { MGNote } from "../../../designSystem/components/MGNote";
import { ModuleBottomSheet } from "./components/ModuleBottomSheet";
import { moduleOptions, TGender, TModuleOption } from "./components/ModuleBottomSheet/types";
import { MGSubtitle } from "../../../designSystem/components/MGSubtitle";
import { MGToast } from "../../../designSystem/components/MGToast";
import { MGDocumentListItem } from "../../../designSystem/components/MGDocumentList";
import { TPdfViewerDocument } from "../../PdfViewer/types";
import { BackgroundForm } from "../../../components/BackgroundForm";

type TModuleStepProps = {
  formHealth: TFormHealth;
  selectedModuleGender?: TGender;
  recommendedModule?: TModuleOption;
  onContinue: (
    formHealth: TFormHealth,
    selectedModuleGender: TGender,
    recommendedModule: TModuleOption,
  ) => void;
  onOpenPdf: (document: TPdfViewerDocument) => void;
};

export const ModuleStep = ({
  formHealth,
  onOpenPdf,
  selectedModuleGender,
  recommendedModule: module,
  onContinue,
}: TModuleStepProps) => {
  const { isMobileAuth, selectedUser } = useMyMontepioContext();

  const [openInfo, setOpenInfo] = useState(false);
  const [openEditModule, setEditModule] = useState(false);

  const [recommendedModule, setRecommendedModule] = useState<TModuleOption | undefined>(module);
  const [selectedModule, setSelectedModule] = useState<TModuleOption>();
  const [selectedGender, setSelectedGender] = useState<TGender>(
    selectedModuleGender ?? formHealth.gender!,
  );

  useEffect(() => {
    if (!!formHealth.moduleType) {
      const module = moduleOptions.find((module) => module.value === formHealth.moduleType);

      setSelectedModule(module);
      return;
    }

    const age = selectedUser?.age ?? calculateAge(formHealth.dateOfBirth!);

    const modulesForGender = moduleOptions.filter(
      (option) => option.gender === formHealth.gender || option.gender === "OTHER",
    );

    let module;

    if (age <= 30) module = modulesForGender.find((module) => module.value === "A1");

    if (age > 30 && age <= 50 && formHealth.gender === "MALE") {
      module = modulesForGender.find((module) => module.value === "M2");
    }
    if (age > 30 && age <= 50 && formHealth.gender === "FEMALE") {
      module = modulesForGender.find((module) => module.value === "F2");
    }

    if (age > 50 && formHealth.gender === "MALE") {
      module = modulesForGender.find((module) => module.value === "M3");
    }
    if (age > 50 && formHealth.gender === "FEMALE") {
      module = modulesForGender.find((module) => module.value === "F3");
    }

    console.log("module", module);

    setSelectedModule(module);
    setRecommendedModule(module);
  }, [formHealth.moduleType, formHealth.dateOfBirth, formHealth.gender, selectedUser?.age]);

  const onDone = useCallback(() => {
    onContinue(
      { ...formHealth, moduleType: selectedModule?.value },
      selectedGender,
      recommendedModule!,
    );
  }, [onContinue, formHealth, selectedModule?.value, selectedGender, recommendedModule]);

  const calculateAge = (birthDateInput: string | Date): number => {
    const birthDate = new Date(birthDateInput);
    const today = new Date();

    if (isNaN(birthDate.getTime())) {
      throw new Error("Invalid birth date provided.");
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <MGLabel style={{ marginTop: 8, marginBottom: 32 }} labelStyleDefinition={"80-03"}>
        {"Texto a definir"}
      </MGLabel>

      <BackgroundForm>
        {recommendedModule?.value === selectedModule?.value && (
          <MGNote
            icon="info"
            description="... módulo à sua medida/ ao seu perfil"
            backgroundColor="transparent"
            style={{ marginBottom: 32 }}
          />
        )}

        <MGSubtitle style={{ marginBottom: 16 }} subtitleStyleDefinition={"50-06"}>
          {recommendedModule?.value === selectedModule?.value
            ? "Módulo Recomendado"
            : "Módulo Selecionado"}
        </MGSubtitle>

        <MGCard backgroundColor="white" shadow="10">
          <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <MGText textStyleDefinition="body-100-05" color="text-primary-03" style={{ flex: 1 }}>
              {selectedModule?.moduleLabel}
            </MGText>
            <InfoIcon color={MGColors["neutral-600"].hex} onClick={() => setOpenInfo(true)} />
          </div>

          <MGText textStyleDefinition="body-200-03" style={{ marginBottom: 4 }}>
            {selectedModule?.genderLabel}
          </MGText>
          <MGLabel labelStyleDefinition="100-06">{selectedModule?.name}</MGLabel>

          <div
            style={{
              justifyContent: "center",
              display: "flex",
              marginTop: 16,
            }}>
            <MGButton size="sm" appearance="tertiary" onClick={() => setEditModule(true)}>
              {"Editar módulo"}
            </MGButton>
          </div>
        </MGCard>
      </BackgroundForm>

      <div style={isMobileAuth ? { height: 32 } : undefined} />

      <MGButton
        fullWidth={isMobileAuth}
        className={`${styles.buttonFooter} ${isMobileAuth ? styles.buttonFooterFixed : ""}`}
        onClick={onDone}>
        {"Continuar"}
      </MGButton>

      <MGResponsiveSheet open={openInfo} title={"Módulos Saúde"} onClose={() => setOpenInfo(false)}>
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <MGText textStyleDefinition="body-100-03">Detalhes e informação geral</MGText>
          <MGSubtitle subtitleStyleDefinition="100-06">Detalhes módulo I</MGSubtitle>
          <ul style={{ paddingInlineStart: 24, marginBottom: 0 }}>
            <li>
              <MGText textStyleDefinition="body-100-03">Detalhes módulo I</MGText>
            </li>
            <li>
              <MGText textStyleDefinition="body-100-03">Detalhes módulo I</MGText>
            </li>
            <li>
              <MGText textStyleDefinition="body-100-03">Detalhes módulo I</MGText>
            </li>
            <li>
              <MGText textStyleDefinition="body-100-03">Detalhes módulo I</MGText>
            </li>
          </ul>
          <MGSubtitle subtitleStyleDefinition="100-06">Mais informação</MGSubtitle>
          <MGDocumentListItem
            appearance="white"
            name={"Modulos de Saúde"}
            extension="pdf"
            additionalInfo={{ type: "PDF" }}
            onDownloadClick={() =>
              onOpenPdf({
                url: `${process.env.PUBLIC_URL}/pdfs/estatutos.pdf`,
                id: "estatutos",
                title: "Estatutos",
              })
            }
          />
        </div>
      </MGResponsiveSheet>

      <MGResponsiveSheet
        open={openEditModule}
        title={"Alterar módulo de faixa etária"}
        onClose={() => setEditModule(false)}>
        <ModuleBottomSheet
          formHealth={formHealth}
          module={selectedModule}
          recommendedModule={recommendedModule}
          gender={selectedGender}
          onSave={(module, gender) => {
            setSelectedModule(module);
            setSelectedGender(gender);
            setEditModule(false);
            MGToast.success({
              title: "Módulo alterado com sucesso",
              message: "Novo módulo selecionado",
            });
          }}
          onClose={() => setEditModule(false)}
        />
      </MGResponsiveSheet>
    </div>
  );
};
